import * as NomenclatureStoragesActions from "./nomenclature-storage.actions";

import {createEntityAdapter, EntityAdapter} from "@ngrx/entity";
import {createReducer, on} from "@ngrx/store";
import {StoredComponentHelperService} from '@common/helpers/stored-component.helper.service';
import {NomenclatureStorage, NomenclatureStorageSearchParams} from '@accounting/_common/models/nomenclature-storage.models';
import {EntityStateModel} from '@common/models/entity-state.models';
import {SetAllInitialStates} from '@common/store/actions/core.actions';

export const nomenclatureStorageFeatureKey = 'nomenclatureStorage';

export function sortEntities(a: NomenclatureStorage, b: NomenclatureStorage) {
    return a.name!.localeCompare(b.name!);
}

export const adapter: EntityAdapter<NomenclatureStorage> = createEntityAdapter<NomenclatureStorage>({
    sortComparer: sortEntities
});

export type NomenclatureStorageState = EntityStateModel<NomenclatureStorage, NomenclatureStorageSearchParams>;

export const initialState: NomenclatureStorageState = adapter.getInitialState({
    listLoading: 'listLoading',
    pagination: {},
    itemsLoading: [],
});

export const nomenclatureStorageReducer = createReducer(
    initialState,

    on(NomenclatureStoragesActions.CheckState, (state, action) => {
        if (action.payload.modalView) {
            return adapter.removeAll({
                ...state,
                savedFilter: !action.payload.close && state.filter ? state.filter : undefined,
                pagination: initialState.pagination,
                filter: action.payload.close ? state.savedFilter : undefined,
            });
        } else {
            if (action.payload.close) {
                return adapter.setAll(action.payload.items, {
                    ...state,
                    pagination: {...state.pagination, number: 0},
                    componentOpened: false
                });
            }

            return {...state, componentOpened: true};
        }
    }),

    on(NomenclatureStoragesActions.LoadList, (state, action) => ({
        ...state,
        listLoading: StoredComponentHelperService.updateListLoading(action.payload, state.ids)
    })),

    on(NomenclatureStoragesActions.LoadListSuccess, (state, action) => {
        return action.payload.clearState
            ? adapter.setAll(action.payload.items, {
                ...state,
                pagination: action.payload.pagination,
                listLoading: undefined

            })
            : adapter.upsertMany(action.payload.items, {
                ...state,
                pagination: action.payload.pagination,
                listLoading: undefined
            });
    }),

    on(NomenclatureStoragesActions.LoadListFailure, (state) => ({
        ...state,
        listLoading: undefined
    })),

    on(NomenclatureStoragesActions.SetFilter, (state, action) => ({
        ...state,
        filter: action.payload
    })),

    on(NomenclatureStoragesActions.SetPagination, (state, action) => ({
        ...state,
        pagination: {...state.pagination, number: action.payload ? StoredComponentHelperService.setNextPage(state.pagination, state.ids?.length) : 0}
    })),

    on(NomenclatureStoragesActions.SetLoading, (state, action) => ({
        ...state,
        listLoading: action.payload
    })),

    on(NomenclatureStoragesActions.SetItemsLoading, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id, action.payload.isStart)
    })),

    on(NomenclatureStoragesActions.UpdateItemSuccess, (state, action) => {
        const savedItemAvailableByFilter = isAvailableByFilter(state.filter, action.payload);
        if (savedItemAvailableByFilter) {
            let previousMainStorage: NomenclatureStorage = {};
            if (action.payload?.main) {
                Object.values(state.entities).map((storage) => {
                    if (storage?.main) {
                        previousMainStorage = {...storage, main: false};
                    }
                });
            }

            return adapter.setMany(previousMainStorage?.id ? [action.payload!, previousMainStorage] : [action.payload!], {
                ...state,
                itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
                pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids)}
            });
        }

        return adapter.removeOne(action.payload.id!, {
            ...state,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
            pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids, true)}
        });
    }),

    on(NomenclatureStoragesActions.DeleteItem, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, true)
    })),

    on(NomenclatureStoragesActions.DeleteItemSuccess, (state, action) => {
        return adapter.removeOne(action.payload.id!, {
            ...state,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
            pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids, true)}
        });
    }),

    on(NomenclatureStoragesActions.DeleteItemFailure, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false)
    })),

    on(NomenclatureStoragesActions.SetInitialState, SetAllInitialStates, () => ({...initialState}))
);

export const {selectAll: selectAllEntities} = adapter.getSelectors();

const isAvailableByFilter = (searchParams: NomenclatureStorageSearchParams | undefined, item: NomenclatureStorage): boolean => {

    if (!searchParams?.includeDeleted && item.archived) {
        return false;
    }

    if (!searchParams) {
        return true;
    }

    if (searchParams.query && item.name && item.name.toLowerCase().indexOf(searchParams.query.toLowerCase()) == -1) {
        return false;
    }


    return true;
};
